<script setup>
import { ref, onMounted, computed, onUnmounted, nextTick } from "vue";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { formatTime, formatDate } from "@/utils/formatter";
import BaseButtonUploadReceipt from "@/components/ui/BaseButtonUploadReceipt.vue";
import BaseTooltip from "@/components/ui/BaseTooltip.vue";
import ProfileEmpty from "@/components/profile/ProfileEmpty.vue";
import { SERVICE_OUTAGE } from "@/utils/serviceOutage";
import { trackAdriver } from "@/utils/metrika";

const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();

const receipts = ref(null);

const statusReceipt = {
	arrived: { title: "На проверке", icon: "arrived" },
	approved: { title: "Одобрен", icon: "approved" },
	rejected: { title: "Отклонен", icon: "rejected" },
	won: { title: "Выиграл", icon: "won" },
};

const statusReceipts = (status) => {
	return statusReceipt[status];
};

const overflowAuto = computed(() => (storeRequests.userReceiptReults.length >= 4 ? "--overflow-auto list-scrollbar " : ""));
const placementStyle = computed(() => {
	if(storeSettings.widthWindow <= 576){
		return 'right';
	}
	if(storeSettings.widthWindow <= 1024){
		return 'left-bottom';
	} 
	return 'right';
});

const scrollContainer = ref(null);
const sentinel = ref(null);
let observer = null;

const setupObserver = async () => {
	await nextTick();

	if (!sentinel.value || !scrollContainer.value) return;

	observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0];
			if (entry.isIntersecting && !storeRequests.loading && !storeRequests.allLoaded) {
				storeRequests.userReceipts();
			}
		},
		{
			root: scrollContainer.value,
			rootMargin: "0px 0px 10px 0px",
			threshold: 0,
		}
	);

	observer.observe(sentinel.value);
};

onMounted(async () => {
	await storeRequests.userReceipts();
	setupObserver();
	const script = document.createElement("script");
	const scriptPath = "/libs/spotlight.bundle.js";
	script.src = scriptPath;
	receipts.value.append(script);
});

onUnmounted(() => {
	if (observer && sentinel.value) {
		observer.unobserve(sentinel.value);
		observer.disconnect();
	}
});
</script>
<template>
	<div class="user-receipts" ref="receipts">
		<h2 :class="['user-receipts__title profile-title', { '--outage': SERVICE_OUTAGE }]">Загруженные чеки</h2>
		<div class="user-receipts__outage" v-if="SERVICE_OUTAGE">
			<p class="user-receipts__outage-text">Из-за сбоя в&nbsp;работе облачного провайдера Яндекс некоторые функции сайта временно недоступны. Мы&nbsp;работаем над восстановлением.</p>
			<BaseButtonUploadReceipt type-btn="upload_check_last_lk" @click="trackAdriver('upload-receipt-lc')" v-if="!storeSettings.isTelegramPage" class="user-receipts__outage-btn">Загрузить чек</BaseButtonUploadReceipt>
		</div>
		<div class="user-receipts__holder" v-if="storeRequests.userReceiptReults.length">
			<ul :class="['user-receipts__list spotlight-group', , overflowAuto]" ref="scrollContainer">
				<li class="user-receipts__item" v-for="receipt of storeRequests.userReceiptReults" :key="receipt.id">
					<div class="user-receipts__image spotlight" :data-src="receipt.images[0]">
						<svg width="38" height="38"><use href="#eye"></use></svg>
						<img class="full-img" width="40" height="40" :src="receipt.images[0]" alt="изображение" />
					</div>

					<div class="user-receipts__body">
						<span class="user-receipts__number">{{ receipt.number }}</span>
						<div class="user-receipts__date">
							<span>{{ formatDate(receipt.upload_date) }}</span>
							<span>{{ formatTime(receipt.upload_date) }}</span>
						</div>
						<div :class="['user-receipts__status', { '--rejected': receipt.status === 'rejected' }]">
							<BaseTooltip v-if="receipt.status === 'rejected'" :placement="placementStyle" :content="receipt.reject_reason" :index="receipt.id">
								<svg width="24" height="24"><use href="#info"></use></svg>
							</BaseTooltip>
							<span>{{ statusReceipts(receipt.status).title }}</span>
						</div>

						
					</div>
				</li>

				<!-- Элемент, за которым мы наблюдаем -->
				<div ref="sentinel" class="sentinel" style="position: relative" v-if="!storeRequests.allLoaded">
					<img class="form__loader" style="position: absolute; left: 50%; transform: translateX(-50%)" width="36" height="36" src="@/assets/img/icons/loader.svg" alt="изображение" />
				</div>
			</ul>
		</div>
		<ProfileEmpty :active-btn="true" type-btn="upload_check_last_lk" v-else-if="!SERVICE_OUTAGE">Загрузите свой первый чек.</ProfileEmpty>	
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.user-receipts {
	&__holder {
		
	}
	
	&__title {
		margin-bottom: 4.7rem;
		@media (max-width: vars.$x480) {
			margin-bottom: 2.2rem;
		}
		// отступы от строк браузера под макет с обрезкой текста до прописных
		&.--outage {
			margin-bottom: 3.06rem;
			@media (max-width: vars.$x480) {
				margin-bottom: 3.25rem;
			}
		}
	}

	&__outage {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 2.7rem;
		// при сбое под текстом и кнопкой показываем уже загруженные чеки
		& + .user-receipts__holder {
			margin-top: 4rem;
			@media (max-width: vars.$x480) {
				margin-top: 2.4rem;
			}
		}
	}

	&__outage-text {
		font-weight: 500;
		font-size: 2rem;
		line-height: 120%;
		color: var(--text-inverse-100);
	}

	&__outage-btn {
		@media (max-width: vars.$x480) {
			width: 100%;
		}
	}
	
	&__list {
		width: 100%;
		display: grid;
		gap: 1.6rem;
		@media (max-width: vars.$x425) {
			gap: 1rem;
		}
		&.--overflow-auto {
			max-height: 36.8rem;
			padding-right: 2rem;
		}
	}
	&__item {
		@extend .base-bg;
		position: relative;
		display: grid;
		grid-template-columns: 4rem 1fr;
		align-items: center;
		gap: 2.4rem;
		font-weight: 400;
		font-size: 2.4rem;
		line-height: 140%;
		border: 1px solid var(--border-accent-2-3);
		border-radius: 2.4rem;
		padding: 2.1rem 2.4rem;
		@media (max-width: vars.$xx) {
			grid-template-columns: 14rem 1fr;
			gap: 16px;
		}
		@media (max-width: vars.$x480) {
			grid-template-columns: 5.6rem 1fr;
			border-radius: 1.6rem;
			padding: 10px;
			font-size: 1.6rem;
		}
		@media (hover: hover) {
			&:hover {
				z-index: 2;
			}
		}
		&:has(.tooltip-wrapper.active) {
			z-index: 2;
		}
	}
	
	&__image {
		position: relative;
		border: 1px solid var(--text-inverse-100);
		border-radius: 0.8rem;
		width: 100%;
		aspect-ratio: 1/1;
		overflow: hidden;
		cursor: pointer;
		@media (max-width: vars.$x480) {
			border-radius: 0.9rem;
			width: 5.6rem;
			height: 5.6rem;
		}
		svg {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			width: 3.8rem;
			height: 3.8rem;
			stroke: transparent;
			opacity: 0;
			transition: var(--transition) ease-in;
		}
		@media (hover: hover) {
			&:hover {
				svg {
					opacity: 1;
				}
			}
		}
	}
	
	&__body {
		display: grid;
		grid-template-columns: 19rem 1fr 1fr;
		gap: 1.2rem;
		@media (max-width: vars.$t) {
			grid-template-columns: 1fr 1fr 1fr;
		}
		@media (max-width: vars.$xx) {
			grid-template-columns: 1fr 1fr;
			gap: .3rem;
		}
	}
	
	&__number {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		@media (max-width: vars.$x480) {
			// max-width: 70px;
		}
	}
	&__status {
		width: fit-content;
		position: relative;
		display: flex;
		align-items: center;
		gap: 1rem;
		@media (max-width: vars.$xx) {
			    grid-column: 1 / 3;
				gap: .5rem;
		}

		svg {
			stroke: transparent;
			@media (max-width: vars.$x480) {
				width: 1.6rem;
				height: 1.6rem;
			}
		}
	}
	&__date {
		--gap: 14px;
		display: flex;
		align-items: center;
		justify-self: start;
		gap: var(--gap);
		@media (max-width: vars.$xx) {
		}
		@media (max-width: vars.$x480) {
		}
		@media (max-width: vars.$x425) {
		}
		span {
			// color: rgba(14, 14, 14, 0.4);
			&:first-child {
				display: flex;
				align-items: center;
				gap: var(--gap);
	
				&::after {
					content: "";
					border-radius: 100%;
					background-color: currentColor;
					width: 0.4rem;
					height: 0.4rem;
				}
			}
			&:last-child {
			}
		}
	}
	&__more-show {
		margin-top: 6rem;
		@media (max-width: vars.$x480) {
			width: 100%;
			margin-top: 3.6rem;
		}
	}
	&__btn {
		margin: 2rem auto 0;
		display: flex;
		@media (max-width: vars.$x480) {
			width: 100%;
		}
	}
}

</style>
